import {
  defineStore,
  storeToRefs,
} from 'pinia';
import api from '@/services/api';
import technical_specification_model from '../models/technical_specification_model';
import technical_specification_filters_model from '../models/technical_specification_filters_model';
import technical_specification_producto_model from '../models/technical_specification_producto_model';
import { useElementoBaseStore } from '@/stores/elemento_base/elemento_base_store';

import initial_delivery_model from '@/shared/models/delivery_model.js';
import { getParamsUrl } from '../technical_specification_utils';
import { useAuthStore } from '../../auth/auth_store';
import elemento_grupo_service from '@/services/elemento_grupo_service';
import { Modules } from '@/enum/modules';
import { ElementoBase } from '@/enum/elemento_base';
import { Proceso } from '@/enum/proceso';
import { useConfiguracionStore } from '@/stores/configuracion-general.js';
import { generarElemento } from '@/shared/generators/genericGenerator';
import { useEstadoSolicitudStore } from '@/stores/estado-solicitud.js';
import getDate from '@/shared/components/utils.js';
import { Role } from '@/enum/roles_enum.js';

export const useTechnicalSpecificationStore = defineStore('technicalSpecificationStore', {
  state: () => ({
    especificacionTecnica: JSON.parse(JSON.stringify(technical_specification_model)),
    filters: { ...technical_specification_filters_model },
    validarEspecificacionTecnicaCampos: {
      Referencia: true,
      CodigoCliente: true,
      ClienteDescripcion: true,
      MedidaEje: true,
      MedidaDesarrollo: true,
      FamiliaMaterial: true,
      Forma: true,
      GapEje: true,
      GapDesarrollo: true,
      EtiquetasDesarrollo: true,
      AnchoMaterial: true,
      Sellado: true,
      Solapa: true,
      Base: true,
      MargenSuperior: true,
    },
    // Nombres de los campos que fallaron en la última validación, para poder decirle al
    // usuario qué falta en vez de un "revisar formulario" a secas.
    camposEspecificacionTecnicaInvalidos: [],
    especificacionesTecnicas: [],
    page: 1,
    enabledCreateRequest: false,
  }),
  getters: {
    estados() {
      const estadoSolicitudStore = useEstadoSolicitudStore();
      return estadoSolicitudStore.estadoSolicitudList;
    },
  },
  actions: {
    async findAllEspecificacionesTecnicasHTTP(params) {
      const authStore = useAuthStore();
      const usuario = authStore.usuario();
      const esEjecutivoDeVentas = usuario.roles.some(
        (rol) => rol.Codigo === Role.EJECUTIVO_DE_VENTAS,
      );

      if (esEjecutivoDeVentas) {
        this.filters.CodigoUsuarioCreacion = usuario.Codigo;
      }

      const filters = { ...this.filters, page: +this.page, ...params };
      const result = await api.get(`/especificacion-tecnica?${getParamsUrl(filters)}`);
      if (result && result.data && result.data.length > 0) {
        this.especificacionesTecnicas.push(...result.data);
        this.setPage(this.page + 1);

        this.filteredToCreateRequest(filters);
        return true;
      }
      return false;
    },
    async getHistorialVersionesHTTP(codigoBase) {
      try {
        const response = await api.get(`/especificacion-tecnica/historial/${codigoBase}`);
        return response.data || [];
      } catch (error) {
        console.error('Error al obtener historial:', error);
        return [];
      }
    },
    async searchTechnicalSpecificationHTTP(query) {
      const url = `/especificacion-tecnica?Search=${encodeURIComponent(query)}&page=1`;
      const result = await api.get(url);
      if (result && result.data && result.data.length > 0) {
        return [...result.data];
      }
    },
    async cloneEspecificacionTecnicaHTTP(codigo) {
      try {
        const authStore = useAuthStore();
        const usuario = authStore.usuario();

        const payload = {
          UsuarioCreacion: { Codigo: usuario.Codigo },
        };

        const response = await api.post(`/especificacion-tecnica/clone/${codigo}`, payload);

        if (response.status === 200 || response.status === 201) {
          return response.data;
        }
      } catch (error) {
        console.error('Error en cloneEspecificacionTecnicaHTTP:', error);
        throw error;
      }
    },
    filteredToCreateRequest(params) {
      if (!this.especificacionesTecnicas || this.especificacionesTecnicas.length === 0) {
        this.enabledCreateRequest = false;
        return;
      }

      if (!params || !params.Cliente || params.Cliente === '') {
        this.enabledCreateRequest = false;
        return;
      }

      this.enabledCreateRequest = this.especificacionesTecnicas.every((especificacion) => {
        return especificacion.CodigoCliente === this.especificacionesTecnicas[0].CodigoCliente;
      });
    },
    async firstEspecificacion() {
      this.setPage(1);
      this.emptyList();
      this.resetFilter();
      await this.findAllEspecificacionesTecnicasHTTP();
    },
    async createEspecificacionTecnicaHTTP() {
      const response = await api.post('/especificacion-tecnica', this.especificacionTecnica);
      if (response.status === 200 || response.status === 201) {
        return response;
      }
    },
    async updateEspecificacionTecnicaHTTP() {
      const codigo = this.especificacionTecnica.Codigo;
      // Crear una copia sin propiedades que podrían causar conflictos
      const { Cotizacion, ...especificacionTecnicaPayload } = this.especificacionTecnica;
      const response = await api.patch(
        `/especificacion-tecnica/${codigo}`,
        especificacionTecnicaPayload,
      );
      if (response.status === 200) {
        return response;
      }
    },
    async findEspecificacionTecnicaPorCodigoHTTP(codigo) {
      try {
        const result = await api.get(`/especificacion-tecnica/${codigo}`);
        if (result && result.data) {
          if (result.data) {
            if (!result.data.Cliente) {
              result.data.Cliente = { Descripcion: '' };
            }
          }
          this.especificacionTecnica = result.data;
        }
      } catch (error) {
        console.error('Error en findEspecificacionTecnicaPorCodigoHTTP:', error);
      }
    },
    addEntrega() {
      this.especificacionTecnica.EspecificacionTecnicaEntregas &&
        this.especificacionTecnica.EspecificacionTecnicaEntregas.push({
          ...initial_delivery_model,
          CodigoEspecificacionTecnica: null,
        });
    },
    addProducto() {
      this.especificacionTecnica.EspecificacionTecnicaProducto.push({
        ...technical_specification_producto_model,
      });
    },
    updateProducto(data) {
      this.especificacionTecnica.EspecificacionTecnicaProducto[data.index] = data.producto;
    },
    removeProducto(index) {
      const productos = this.especificacionTecnica.EspecificacionTecnicaProducto;
      if (productos && productos.length) {
        productos.splice(index, 1);
        this.especificacionTecnica.EspecificacionTecnicaProducto = productos.slice(index);
      }
    },
    resetValidaciones() {
      Object.keys(this.validarEspecificacionTecnicaCampos).forEach((key) => {
        this.validarEspecificacionTecnicaCampos[key] = true;
      });
      this.camposEspecificacionTecnicaInvalidos = [];
    },
    validarCampos() {
      const elementoBaseStore = useElementoBaseStore();
      const { elementoBaseConfig } = storeToRefs(elementoBaseStore);
      const primerElemento = this.especificacionTecnica.EspecificacionTecnicaElemento[0];

      this.validarEspecificacionTecnicaCampos = {
        Base: true,
        ClienteDescripcion: this.especificacionTecnica.EsGlobal
          ? true
          : !!this.especificacionTecnica.Cliente?.Descripcion,
        CodigoCliente: this.especificacionTecnica.EsGlobal
          ? true
          : !!this.especificacionTecnica.CodigoCliente,
        EtiquetasDesarrollo: true,
        EtiquetasEje: true,
        FamiliaMaterial: true,
        Forma: true,
        GapDesarrollo: true,
        GapEje: true,
        MargenSuperior: true,
        MedidaDesarrollo: true,
        MedidaEje: true,
        Referencia: !!this.especificacionTecnica.Referencia,
        Sellado: true,
        Solapa: true,
        AnchoMaterial: true,
      };

      const codigoElementoBase = primerElemento?.ElementoTipo?.CodigoElementoBase;

      // COTI-507: extrusion no usa los campos del elemento base (suaje, medidas, familia
      // de material), asi que se saltean todas esas validaciones y en su lugar se validan
      // los datos del film resultante, que son los que el cotizador necesita.
      if (codigoElementoBase === ElementoBase.EXTRUSION) {
        this.validarEspecificacionTecnicaCampos.FamiliaMaterial = true;
        this.validarCamposExtrusion(primerElemento);
      } else {
        if (elementoBaseConfig.value.campos.mostrarMedidaEje) {
          this.validarEspecificacionTecnicaCampos.MedidaEje = !!primerElemento.MedidaEje;
        }
        if (elementoBaseConfig.value.campos.mostrarMedidaDesarrollo) {
          this.validarEspecificacionTecnicaCampos.MedidaDesarrollo =
            !!primerElemento.MedidaDesarrollo;
        }
        if (elementoBaseConfig.value.campos.mostrarAnchoMaterial) {
          this.validarEspecificacionTecnicaCampos.AnchoMaterial = !!primerElemento.AnchoMaterial;
        } else {
          this.validarEspecificacionTecnicaCampos.AnchoMaterial = true;
        }
        // COTI-422: Se definio que el sistema no valide gaps
        // if(elementoBaseConfig.value.campos.mostrarGapEje) {
        //   this.validarEspecificacionTecnicaCampos.GapEje = !!primerElemento.GapEje
        // }
        // if(elementoBaseConfig.value.campos.mostrarGapDesarrollo) {
        //   this.validarEspecificacionTecnicaCampos.GapDesarrollo = !!primerElemento.GapDesarrollo
        // }
        if (elementoBaseConfig.value.campos.mostrarEtiquetasAncho) {
          this.validarEspecificacionTecnicaCampos.EtiquetasEje = !!primerElemento.EtiquetasEje;
        }
        if (elementoBaseConfig.value.campos.mostrarEtiquetasDesarrollo) {
          this.validarEspecificacionTecnicaCampos.EtiquetasDesarrollo =
            !!primerElemento.EtiquetasDesarrollo;
        }

        if (
          ![
            ElementoBase.TERMO_ENCOGIBLE,
            ElementoBase.CAJA,
            ElementoBase.CONSUMIBLE,
            ElementoBase.FLEXIBLE,
            ElementoBase.BOLSAS_FUELLE,
            ElementoBase.BOLSAS_BOX,
            ElementoBase.OFFSET,
          ].includes(codigoElementoBase)
        ) {
          this.validarEspecificacionTecnicaCampos.FamiliaMaterial =
            !!primerElemento.MateriaPrimaFamilia?.Codigo;
        } else if ([ElementoBase.BOLSAS_BOX].includes(codigoElementoBase)) {
          if (elementoBaseConfig.value.campos.mostrarMargenSuperior) {
            this.validarEspecificacionTecnicaCampos.Solapa = !!primerElemento.Solapa;
          }
          this.validarEspecificacionTecnicaCampos.Sellado = !!primerElemento.Sellado;
          this.validarEspecificacionTecnicaCampos.Base = !!primerElemento.Base;
        } else if ([ElementoBase.BOLSAS_FUELLE].includes(codigoElementoBase)) {
          this.validarEspecificacionTecnicaCampos.Base = !!primerElemento.Base;
        } else {
          this.validarEspecificacionTecnicaCampos.FamiliaMaterial = true;
        }
      }

      return this.verificarValidacionPropiedades();
    },
    /**
     * Datos del film resultante que el cotizador necesita cuando el elemento se resuelve
     * por dimensionamiento continuo. Sin el gramaje —cargado directo o derivado de
     * Densidad x Espesor, según calculo_material_extrusion— no puede derivar el largo del
     * pedido y falla con "desarrolloMm debe ser > 0".
     *
     * El ancho del elemento no se valida como campo propio: en extrusión no se carga a
     * mano, sale de la sumatoria de los anchos de las bobinas del proceso. Lo que se exige
     * son las bobinas. Mismo criterio que en cotización (prices_store).
     */
    validarCamposExtrusion(elemento) {
      const { calculoMaterialExtrusion } = useConfiguracionStore();
      const esCalculoPorGramaje = calculoMaterialExtrusion === 'gramaje';

      const procesos = elemento.EspecificacionTecnicaElementoProceso || [];
      const procesosExtrusion = procesos.filter(
        (p) => (p.Proceso?.Codigo ?? p.CodigoProceso) === Proceso.EXTRUSION,
      );
      // Todavía no se agregó el proceso de extrusión: no hay film del que hablar.
      if (procesosExtrusion.length === 0) return;

      // Mismo criterio que esSoloExtrusion en el cotizador: si la extrusión convive con
      // otros procesos, el elemento se dimensiona por pliego físico y el gramaje del film
      // no entra en el cálculo, así que no hay por qué exigirlo.
      if (procesos.length !== procesosExtrusion.length) return;

      const cargado = (valor) =>
        valor !== null && valor !== undefined && valor !== '' && Number(valor) > 0;
      const todos = (predicado) => procesosExtrusion.every(predicado);

      this.validarEspecificacionTecnicaCampos.MaterialResultante = todos((p) =>
        cargado(p.CodigoMaterialResultante ?? p.MaterialResultante?.Codigo),
      );
      // De acá sale el ancho del material, que el cotizador exige > 0.
      this.validarEspecificacionTecnicaCampos.BobinasExtrusion = todos((p) =>
        (p.ElementoProcesoBobinas || []).some((bobina) => cargado(bobina?.Ancho)),
      );

      if (esCalculoPorGramaje) {
        this.validarEspecificacionTecnicaCampos.GramajeResultante = todos((p) =>
          cargado(p.GramajeResultante),
        );
      } else {
        this.validarEspecificacionTecnicaCampos.EspesorResultante = todos((p) =>
          cargado(p.EspesorResultante),
        );
        this.validarEspecificacionTecnicaCampos.DensidadResultante = todos((p) =>
          cargado(p.DensidadResultante),
        );
      }
    },
    verificarValidacionPropiedades() {
      this.camposEspecificacionTecnicaInvalidos = Object.keys(
        this.validarEspecificacionTecnicaCampos,
      ).filter((property) => !this.validarEspecificacionTecnicaCampos[property]);

      if (this.camposEspecificacionTecnicaInvalidos.length > 0) {
        console.debug(
          `Validación fallida para las propiedades: ${this.camposEspecificacionTecnicaInvalidos.join(', ')}`,
        );
        return false;
      }
      return true;
    },
    setPage(page) {
      this.page = page;
    },
    emptyList() {
      this.especificacionesTecnicas = [];
    },
    setFilters(newFilters) {
      this.filters = { ...newFilters };
    },
    resetFilter() {
      this.filters = { ...technical_specification_filters_model };
    },
    resetEspecificacionTecnica() {
      this.especificacionTecnica = JSON.parse(JSON.stringify(technical_specification_model));
    },
    async setDefaults(codigo) {
      this.especificacionTecnica.FechaEstado = getDate('yyyy/mm/dd');
      this.especificacionTecnica.Estado = { Codigo: 1, Descripcion: 'Pendiente' };

      if (this.estados.length > 0) {
        const pendiente = this.estados.find((estado) => estado.Descripcion === 'Pendiente');
        if (pendiente) {
          this.especificacionTecnica.Estado = { ...pendiente };
        }
      }

      const elementosDefaults = await elemento_grupo_service.getGrupoDefaults(
        codigo,
        Modules.ESPECIFICACION_TECNICA,
      );
      elementosDefaults.forEach((elementoDefault, index) => {
        const elementoGenerated = generarElemento(
          Modules.ESPECIFICACION_TECNICA,
          elementoDefault,
          index,
        );
        this.especificacionTecnica.EspecificacionTecnicaElemento[
          index
        ].EspecificacionTecnicaElementoProceso =
          elementoGenerated.EspecificacionTecnicaElementoProceso;
      });
    },
  },
});
